import { useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle, Check, Copy, Download, FileBox, Loader2, Plug, X } from 'lucide-react';
import {
  addonDownloadUrl, connectionKey, createAddonKey, fetchAddonState, fetchBlendFiles,
  isOutdated, revokeAddonKey, type AddonKey, type BlendFile,
} from './addonApi';
import { falError } from './threeDApi';
import { formatWhen } from './formatWhen';
import { modalBackdropCls, modalCardCls, overlayCls } from './overlay';
import { useLanguage } from '../../contexts/LanguageContext';

// Setting up the add-on, and afterwards, whether it is working.
//
// The other route out to Blender is a file at a time — download, import, place, and the
// same four things again when the part is regenerated. The add-on collapses that into a
// button per part inside Blender, and everything it needs is here: the file to install
// and the key to paste.
//
// WHAT IT CANNOT DO is look at your machine. A browser reads no filesystem, so whether
// the add-on sits in Blender's addons folder is not a question this page can ask. What it
// can be told is what reached the SERVER — every call the add-on makes stamps the key
// with a time and a version — and that turns out to be the better fact: it proves the
// whole chain rather than one step of it.
//
// So there are three screens here, not one. Nothing set up yet: three steps. A key that
// nothing has ever used: the same three steps, with the last one saying the paste has not
// landed. An add-on that has called in: what version, how long ago, and the steps folded
// away behind "set up another machine", because they are read once and then never again.
//
// And under all three, WHICH .blend FILES this notice has — see BlendFileList. It is the
// one question about the add-on that is also a question about the notice, which is why it
// takes a docId when nothing else here does.
export function BlenderAddonModal(
  { docId, onClose }: { docId: string; onClose: () => void },
) {
  const { t, language } = useLanguage();
  const [key, setKey] = useState<AddonKey | null>(null);
  // The add-on version this server is handing out, from the file's own bl_info.
  const [version, setVersion] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // The raw key, held only in this component's state and only until the modal closes.
  // The server cannot answer for it a second time, so a copy button is not a convenience
  // here — it is the only way the value leaves this screen.
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  // Polled, not read once. What this screen reports is a thing that happens on ANOTHER
  // machine — an add-on in Blender calling in — and a modal that read the answer when it
  // opened would go on showing "nothing has used it yet" for as long as it stayed open,
  // however many times the person in front of it did the thing it was asking for. Which
  // is exactly what it did, and the only way out was to close it and open it again.
  //
  // Every four seconds while it is open, and stopped as soon as an add-on has called in:
  // the state it is watching for is one-way, so there is nothing to keep asking about
  // once it has arrived.
  useEffect(() => {
    let alive = true;
    let done = false;
    const read = () => {
      void fetchAddonState()
        .then((state) => {
          if (!alive) return;
          setKey(state.key);
          setVersion(state.version);
          done = Boolean(state.key?.lastUsedAt);
        })
        .catch(() => {})
        .finally(() => alive && setLoading(false));
    };
    read();
    const timer = window.setInterval(() => !done && read(), 4000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const generate = async () => {
    setBusy(true);
    setError('');
    try {
      const made = await createAddonKey();
      setKey(made.key);
      setToken(made.token);
    } catch (e) {
      setError(falError(e, t('notice.addon.keyFailed')));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    setError('');
    try {
      await revokeAddonKey();
      setKey(null);
      setToken('');
    } catch (e) {
      setError(falError(e, t('notice.addon.keyFailed')));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(connectionKey(token));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused (an insecure origin, a browser setting). The
      // field is selectable, so there is still a way to take the value — saying so beats
      // a button that silently did nothing.
      setError(t('notice.addon.copyFailed'));
    }
  };

  // Used at least once. A timestamp on the current row can only belong to the current
  // key, because generating one DELETEs the old row rather than updating it — so there is
  // no stale-timestamp case to guard against, and the green state can arrive while the
  // key is still on screen being copied. Which is exactly when it should arrive.
  const connected = Boolean(key?.lastUsedAt);
  const outdated = connected && isOutdated(key?.addonVersion ?? null, version);

  const steps = (
    <>
      <Step n={1} title={t('notice.addon.step1')}>
        <DownloadButton label="noticeia_blender.py" />
      </Step>
      <Step n={2} title={t('notice.addon.step2')}>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {t('notice.addon.step2Path')}
        </p>
      </Step>
      <Step n={3} title={t('notice.addon.step3')}>
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        ) : token ? (
          <>
            {/* Shown once, and this modal is the only place it exists. Read-only rather
                than disabled: a disabled input cannot be selected, and selecting it is
                the fallback for every browser that refuses the clipboard. */}
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={connectionKey(token)}
                onFocus={(e) => e.currentTarget.select()}
                className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-slate-50 px-2 py-1.5 font-mono text-xs text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
              />
              <button
                type="button"
                onClick={() => void copy()}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {t(copied ? 'notice.addon.copied' : 'notice.addon.copy')}
              </button>
            </div>
            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
              {t('notice.addon.keyOnce')}
            </p>
          </>
        ) : key ? (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {t('notice.addon.keyPending')}
            </p>
            <KeyControls busy={busy} onGenerate={generate} onRevoke={revoke} />
          </>
        ) : (
          <button
            type="button"
            onClick={() => void generate()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-40"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('notice.addon.keyGenerate')}
          </button>
        )}
      </Step>
      <p className="mt-5 border-t border-slate-200 pt-4 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-400">
        {t('notice.addon.then')}
      </p>
    </>
  );

  return (
    <div className={`${overlayCls} ${modalBackdropCls}`} onClick={onClose}>
      <div
        className={`max-h-[85vh] w-full max-w-lg overflow-y-auto p-6 ${modalCardCls}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* The close button is taken out of the flow rather than sitting beside the
            title: centred text with a button on one end of the line is centred within
            what the button left over, which reads as very slightly off. The title's own
            padding keeps it clear of the button on a narrow window. */}
        <div className="relative">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-0 top-0 text-slate-400 transition-colors hover:text-slate-600 dark:hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
          <h2 className="px-8 text-center text-base font-semibold text-slate-900 dark:text-slate-100">
            {t('notice.addon.title')}
          </h2>
        </div>
        {connected ? (
          <>
            <div className="mt-5 rounded-xl border border-blue-300 bg-blue-50 p-4 text-center dark:border-blue-800 dark:bg-blue-950/40">
              <p className="flex items-center justify-center gap-2 text-sm font-semibold text-blue-700 dark:text-blue-300">
                <Plug className="h-4 w-4" />
                {t('notice.addon.connected')}
              </p>
              <p className="mt-1 text-xs text-blue-700/80 dark:text-blue-400/80">
                {t('notice.addon.connectedWhen')}{' '}
                {formatWhen(key?.lastUsedAt as string, language)}
                {key?.addonVersion && ` · ${t('notice.addon.installedVersion')} ${key.addonVersion}`}
              </p>
            </div>

            {outdated && (
              <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-center dark:border-amber-800 dark:bg-amber-950/40">
                <p className="flex items-center justify-center gap-2 text-sm font-semibold text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4" />
                  {t('notice.addon.outdated')} {version}
                </p>
                <p className="mt-1 text-xs text-amber-700/80 dark:text-amber-400/80">
                  {t('notice.addon.outdatedHow')}
                </p>
                <div className="mt-3">
                  <DownloadButton label="noticeia_blender.py" />
                </div>
              </div>
            )}

            <div className="mt-5 text-center">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                {t('notice.addon.keyInstalled')}{' '}
                <span className="font-mono text-xs">…{key?.preview}</span>
              </p>
              <KeyControls busy={busy} onGenerate={generate} onRevoke={revoke} />
            </div>

            {/* Folded away, because it is read once. Still reachable, because a second
                machine is a real thing to want and the steps are the same ones. */}
            {/* text-center on the summary and NOT on the details: the steps inside are a
                numbered list, and centring their prose away from their badges would leave
                each one hanging off its own number. */}
            <details className="mt-5 border-t border-slate-200 pt-4 dark:border-slate-700">
              <summary className="cursor-pointer text-center text-sm font-medium text-slate-500 transition-colors hover:text-blue-600 dark:text-slate-400">
                {t('notice.addon.another')}
              </summary>
              {steps}
            </details>
          </>
        ) : (
          steps
        )}

        <BlendFileList docId={docId} />

        {error && <p className="mt-3 text-xs text-rose-600 dark:text-rose-400">{error}</p>}
      </div>
    </div>
  );
}

// WHICH .blend FILES ARE ASSEMBLING THIS NOTICE. A record and not a registry: nothing
// here can open one of them, because they are on somebody's laptop. What it answers is
// which file to go and open, which has no answer at all once a notice has been put down
// for three weeks and there are four .blend files on a desktop.
//
// It used to sit under the button on the phase itself, where a list of filenames was
// three lines of somebody's home directory in the middle of a 3D panel. Here it is behind
// the same click as the key and the installer — one dialog that is about the add-on — and
// the phase keeps a button.
//
// Polled while the dialog is open, for the reason the key state above is: what it reports
// happens in Blender, on another machine, and a list read once would go on saying "no
// Blender file yet" for as long as it stayed open, however many the person made.
function BlendFileList({ docId }: { docId: string }) {
  const { t, language } = useLanguage();
  // NULL UNTIL THE FIRST ANSWER: `[]` is "no Blender file yet", and it was on screen before
  // anybody had asked. `failed`: the last poll did not come back — said while nothing is known,
  // and a list already read stays up rather than flashing away on one missed poll.
  const [files, setFiles] = useState<BlendFile[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setFiles(null);
    const read = () => {
      void fetchBlendFiles(docId)
        .then((got) => { if (alive) { setFiles(got); setFailed(false); } })
        .catch(() => { if (alive) setFailed(true); });
    };
    read();
    const timer = window.setInterval(read, 4000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [docId]);

  return (
    <div className="mt-5 border-t border-slate-200 pt-4 dark:border-slate-700">
      <p className="text-center text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {t('notice.addon.files')}
      </p>
      {files === null ? (
        failed ? (
          <p className="mt-2 text-center text-xs text-rose-600 dark:text-rose-400">
            {t('notice.addon.filesFailed')}
          </p>
        ) : (
          <Loader2 className="mx-auto mt-2 h-4 w-4 animate-spin text-slate-400 dark:text-slate-500" />
        )
      ) : files.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-1">
          {files.map((f) => (
            <li
              key={`${f.owner ?? ''}/${f.filename}`}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 shadow-sm dark:border-slate-700 dark:bg-slate-800"
            >
              <FileBox className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span className="min-w-0 flex-1 truncate font-mono text-xs text-slate-700 dark:text-slate-200">
                {f.filename}
              </span>
              {/* Last SEEN and not last saved, and the wording says so. Nothing here
                  watches the file: it stops refreshing when it is simply not opened. */}
              <span className="shrink-0 text-[11px] text-slate-500 dark:text-slate-400">
                {f.owner && <span className="mr-1.5">{f.owner}</span>}
                {f.lastSeenAt ? formatWhen(f.lastSeenAt, language) : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-center text-xs text-slate-500 dark:text-slate-400">
          {t('notice.addon.noFiles')}
        </p>
      )}
    </div>
  );
}

function DownloadButton({ label }: { label: string }) {
  return (
    <a
      href={addonDownloadUrl}
      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600 transition-colors hover:border-blue-400 hover:text-blue-600 dark:border-slate-600 dark:text-slate-300"
    >
      <Download className="h-4 w-4" />
      {label}
    </a>
  );
}

// Replacing a key and revoking it, wherever the key is being shown. Both are the same
// two buttons whether the add-on has called in or not, so they are one component rather
// than a pair written twice.
function KeyControls({ busy, onGenerate, onRevoke }: {
  busy: boolean;
  onGenerate: () => Promise<void>;
  onRevoke: () => Promise<void>;
}) {
  const { t } = useLanguage();
  return (
    <div className="mt-3 flex flex-wrap justify-center gap-2">
      <button
        type="button"
        onClick={() => void onGenerate()}
        disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-600 transition-colors hover:border-blue-400 hover:text-blue-600 disabled:opacity-40 dark:border-slate-600 dark:text-slate-300"
      >
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        {t('notice.addon.keyReplace')}
      </button>
      <button
        type="button"
        onClick={() => void onRevoke()}
        disabled={busy}
        className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-400 transition-colors hover:text-rose-600 disabled:opacity-40 dark:text-slate-500"
      >
        {t('notice.addon.keyRevoke')}
      </button>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <div className="mt-5 flex gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500 dark:bg-slate-800 dark:text-slate-400">
        {n}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{title}</p>
        <div className="mt-2">{children}</div>
      </div>
    </div>
  );
}
